import React, { type JSX, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';

import type { RaceTransitionVerificationTone } from './types/SharedTypes';

export type { RaceTransitionVerificationTone };

type Props = {
  title: string;
  tone: RaceTransitionVerificationTone;
  itemCount: number;
  itemLabel: string;
  issueCount?: number;
  summary: ReactNode;
  filter?: ReactNode;
  headerActions?: ReactNode;
  /** Scrollable table area height (default compact for transition dialogs). */
  tableMaxHeightClass?: string;
  children: ReactNode;
};

export function verificationPanelShellClass(tone: RaceTransitionVerificationTone): string {
  if (tone === 'ok') {
    return 'overflow-hidden rounded-xl border border-emerald-200/80 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-emerald-950/40 dark:via-slate-900 dark:to-slate-950 shadow-sm ring-1 ring-emerald-100';
  }
  if (tone === 'warn') {
    return 'overflow-hidden rounded-xl border border-amber-200/80 bg-gradient-to-b from-amber-50 via-white to-white dark:from-amber-950/30 dark:via-slate-900 dark:to-slate-950 shadow-sm ring-1 ring-amber-100/80';
  }
  return 'overflow-hidden rounded-xl border border-gray-200 dark:border-slate-700 bg-gradient-to-b from-sky-50 via-white to-white dark:from-slate-800 dark:via-slate-900 dark:to-slate-950 shadow-sm ring-1 ring-gray-200 dark:ring-slate-700';
}

function badgeClass(tone: RaceTransitionVerificationTone): string {
  if (tone === 'ok') {
    return 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/80 dark:bg-emerald-950/60 dark:text-emerald-200 dark:ring-emerald-800/60';
  }
  if (tone === 'warn') {
    return 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/80 dark:bg-amber-950/60 dark:text-amber-200 dark:ring-amber-800/60';
  }
  return 'bg-slate-100 text-slate-700 ring-1 ring-slate-200/80 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600/60';
}

/** Shared shell for athlete / device verification blocks in race transition dialogs. */
export default function RaceTransitionVerificationPanel(props: Props): JSX.Element {
  const {
    title,
    tone,
    itemCount,
    itemLabel,
    issueCount = 0,
    summary,
    filter,
    headerActions,
    tableMaxHeightClass = 'max-h-52',
    children,
  } = props;
  const plural = itemCount === 1 ? '' : 's';
  const issueSuffix =
    issueCount > 0
      ? ` · ${issueCount} issue${issueCount === 1 ? '' : 's'}`
      : tone === 'ok' && itemCount > 0
        ? ' · all clear'
        : '';

  return (
    <div className={['space-y-3 p-4', verificationPanelShellClass(tone)].join(' ')}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 dark:text-slate-400">{title}</h3>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {headerActions}
          <span
            className={[
              'inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide',
              badgeClass(tone),
            ].join(' ')}
          >
            {itemCount} {itemLabel}
            {plural}
            {issueSuffix}
          </span>
        </div>
      </div>
      <div className="text-xs leading-relaxed">{summary}</div>
      {filter ? <div className="space-y-2">{filter}</div> : null}
      <div className={[tableMaxHeightClass, 'overflow-auto'].join(' ')}>{children}</div>
    </div>
  );
}

export function VerificationSummaryOk({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-emerald-900 dark:text-emerald-100">
      <span
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-200 text-[10px] font-bold text-emerald-900 dark:bg-emerald-900/70 dark:text-emerald-100"
        aria-hidden
      >
        ✓
      </span>
      {children}
    </p>
  );
}

export function VerificationSummaryWarn({ children }: { children: ReactNode }): JSX.Element {
  return (
    <p className="text-xs leading-relaxed text-red-900/90 dark:text-red-200/90">
      <span className="font-semibold text-red-950 dark:text-red-100">Recommended: </span>
      {children}
    </p>
  );
}

export function VerificationSummaryCaution({ detail }: { detail: string }): JSX.Element {
  return (
    <p className="flex items-start gap-2 text-xs font-medium leading-relaxed text-amber-950 dark:text-amber-100">
      <span
        className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 ring-1 ring-amber-300/70"
        aria-hidden
      >
        <AlertTriangle className="h-3.5 w-3.5 text-amber-800" strokeWidth={2.25} />
      </span>
      <span>{detail}</span>
    </p>
  );
}
